import {
  GroupProcess,
  GroupRequirement,
  AnswerQuestion,
  RequirementColumn,
  Requirement,
  Department,
  MonthDictionary,
  Month
} from '../types/data';
import { Report, Column } from '../types/table';
import { ResponseElement } from '../types/response';

export const calculateIndentColumn = (type: Report, data: Column) => {
  switch (type) {
    case 'factory': {
      return data.indentFactory;
    }

    case 'region': {
      return data.indentRegion;
    }

    default:
      return data.indent;
  }
};

export const convertToMegabytes = (bytes: number): number => {
  const megabytes = bytes / (1024 * 1024);
  return Math.round(megabytes);
};

// Type Guards
const isGroupProcess = (
  data:
    | GroupProcess[]
    | GroupRequirement[]
    | AnswerQuestion[]
    | RequirementColumn[]
    | Requirement[],
): data is GroupProcess[] => {
  return (data as GroupProcess[])[0]?.requirements !== undefined;
};

const isGroupRequirement = (
  data:
    | GroupProcess[]
    | GroupRequirement[]
    | AnswerQuestion[]
    | RequirementColumn[]
    | Requirement[],
): data is GroupRequirement[] => {
  return (data as GroupRequirement[])[0]?.groupRequirements !== undefined;
};

const isColumnRequirement = (
  data:
    | GroupProcess[]
    | GroupRequirement[]
    | AnswerQuestion[]
    | RequirementColumn[]
    | Requirement[],
): data is RequirementColumn[] => {
  return (data as RequirementColumn[])[0]?.columnRequirements !== undefined;
};

const isAnswerQuestion = (
  data:
    | GroupProcess[]
    | GroupRequirement[]
    | AnswerQuestion[]
    | RequirementColumn[]
    | Requirement[],
): data is AnswerQuestion[] => {
  return (data as AnswerQuestion[])[0]?.answer !== undefined;
};

const isRequirement = (
  data:
    | GroupProcess[]
    | GroupRequirement[]
    | AnswerQuestion[]
    | RequirementColumn[]
    | Requirement[],
): data is Requirement[] => {
  return (data as Requirement[])[0]?.answers !== undefined;
};

export const filteredAnswers = (
  data:
    | GroupProcess[]
    | GroupRequirement[]
    | AnswerQuestion[]
    | RequirementColumn[],
  departments: Department[],
) => {
  if (isColumnRequirement(data)) {
    return data.map((column) => ({
      ...column,
      columnRequirements: column.columnRequirements.map((section) => ({
        ...section,
        groupRequirements: section.groupRequirements.map((group) => ({
          ...group,
          requirements: group.requirements.map((requirement) => ({
            ...requirement,
            answers: requirement.answers.filter((answer) =>
              departments.some(
                (department) => department.title === answer.department,
              ),
            ),
          })),
        })),
      })),
    }));
  }

  if (isGroupRequirement(data)) {
    return data.map((section) => ({
      ...section,
      groupRequirements: section.groupRequirements.map((group) => ({
        ...group,
        requirements: group.requirements.map((requirement) => ({
          ...requirement,
          answers: requirement.answers.filter((answer) =>
            departments.some(
              (department) => department.title === answer.department,
            ),
          ),
        })),
      })),
    }));
  }

  if (isGroupProcess(data)) {
    return data.map((group) => ({
      ...group,
      requirements: group.requirements.map((requirement) => ({
        ...requirement,
        answers: requirement.answers.filter((answer) =>
          departments.some(
            (department) => department.title === answer.department,
          ),
        ),
      })),
    }));
  }

  if (isAnswerQuestion(data)) {
    return data.filter((answer) =>
      departments.some(
        (department) => department.title === answer.department,
      ),
    );
  }

  return [];
};

export const calculatesAverageValue = (
  data:
    | GroupProcess[]
    | GroupRequirement[]
    | AnswerQuestion[]
    | RequirementColumn[]
    | Requirement[],
  department?: AnswerQuestion['department'],
  isAverage?: boolean,
  factory?: string,
  demand?: string,
  isRegion?: boolean,
): number | 'N/A' => {

  let answersArray: AnswerQuestion[] = [];

  if (isRequirement(data)) {
    answersArray = data
      .filter((requirement) => requirement.title === demand)
      .flatMap((answer) => answer.answersFactory ?? [])
      .flatMap((answer) => answer.answersFactory);
  }

  if (isColumnRequirement(data)) {
    answersArray = data.flatMap((column) =>
      column.columnRequirements.flatMap((section) =>
        section.groupRequirements.flatMap((group) =>
          group.requirements.flatMap((requirement) =>
            isAverage
              ? requirement.answers
              : requirement.answers.filter(
                  (answer) => answer.department === department,
                ),
          ),
        ),
      ),
    );
  }

  if (isGroupRequirement(data)) {
    answersArray = data.flatMap((section) =>
      section.groupRequirements.flatMap((group) =>
        group.requirements.flatMap((requirement) =>
          isAverage
            ? requirement.answers
            : requirement.answers.filter(
                (answer) => answer.department === department,
              ),
        ),
      ),
    );
  }

  if (isGroupRequirement(data) && factory) {
    answersArray = data.flatMap((section) =>
      section.groupRequirements.flatMap((group) =>
        group.requirements.flatMap((requirement) =>
          (requirement.answersFactory ?? [])
            .filter((answerGroup) => answerGroup.title === factory)
            .flatMap((answerGroup) => answerGroup.answersFactory),
        ),
      ),
    );
  }

  if (isGroupRequirement(data) && isRegion) {
    answersArray = data.flatMap((section) =>
      section.groupRequirements.flatMap((group) =>
        group.requirements.flatMap((requirement) =>
          (requirement.answersFactory ?? []).flatMap(
            (answerGroup) => answerGroup.answersFactory,
          ),
        ),
      ),
    );
  }

  if (isGroupProcess(data)) {
    answersArray = data.flatMap((group) =>
      group.requirements.flatMap((requirement) =>
        isAverage
          ? requirement.answers
          : requirement.answers.filter(
              (answer) => answer.department === department,
            ),
      ),
    );
  }

  if (isGroupProcess(data) && factory) {
    answersArray = data.flatMap((group) =>
      group.requirements.flatMap((requirement) =>
        (requirement.answersFactory ?? [])
          .filter((answerGroup) => answerGroup.title === factory)
          .flatMap((answerGroup) => answerGroup.answersFactory),
      ),
    );
  }

  if (isGroupProcess(data) && isRegion) {
    answersArray = data.flatMap((group) =>
      group.requirements.flatMap((requirement) =>
        (requirement.answersFactory ?? []).flatMap(
          (answerGroup) => answerGroup.answersFactory,
        ),
      ),
    );
  }

  if (isAnswerQuestion(data)) {
    answersArray = data;
  }

  const validAnswers = answersArray.filter((answer) => answer.answer !== 'N/A' && !answer.disabled);

  if (validAnswers.length === 0) {
    return 'N/A';
  }

  const filterAnswersArray = answersArray.filter((answer) => !answer.disabled);

  const { sum, count } = filterAnswersArray.reduce(
    (acc, question) => {
      if (question.answer === 'Да') {
        acc.sum += 100;
        acc.count += 1;
      } else if (question.answer === 'Нет') {
        acc.sum += 0;
        acc.count += 1;
      }

      return acc;
    },
    { sum: 0, count: 0 },
  );

  return count > 0 ? Math.round(sum / count) : 0;
};


export const getCurrentMonthAndYear = (data) => {
  const current = +new Date();

  for (const item of data) {
    const start = new Date(item.startDate).setHours(0, 0, 0);
    const end = new Date(item.finishDate).setHours(23, 59, 59);

    if (current >= start && current <= end) {
      return {
        month: MonthDictionary[item.month],
        year: item.year,
        finishDate: item.finishDate
      };
    }
  }

  return null;
}

export const getMonthNumber = (year: number, month: number): number => {
  return year * 12 + month;
};


export const filterByRange = (
  data: ResponseElement[],
  month: number,
  year: number
): ResponseElement[] => {
  const target = getMonthNumber(year, month);

  return data.filter(({ showElement }) => {
    if (!showElement || showElement.length === 0) return true;

    return showElement.some(({ startYear, startMonth, finishYear, finishMonth }) => {
      const start = getMonthNumber(startYear, MonthDictionary[startMonth]);
      const finish = finishYear === null || finishMonth === null ? Infinity : getMonthNumber(finishYear, MonthDictionary[finishMonth]);

      return target >= start && target <= finish;
    });
  });
};

export const isNotStartedQuestionnaire = (data: GroupRequirement[]) => {
  return data.every(block =>
    block.groupRequirements.every(group =>
      group.requirements.every(requirement =>
        requirement.answers.every(answer => answer.answerId === null)
    ))
  );
}

export const findQuestionnaire = (data, year, month, factory, column) => {
  const result = data.find((item) =>
    item.Month === month &&
    item.Year == year &&
    item.factory?.title === factory &&
    item.column?.title === column
  );

  return result ?? {
    completionDate: "В процессе",
    AuthorEnd: "В процессе"
  };
}

export const hasNoAction = (data: GroupRequirement[]) => {
  for (const item of data) {
    for (const group of item.groupRequirements) {
      for (const req of group.requirements) {
        for (const ans of req.answers) {
          if (ans.answer === "Нет" && ans.actions === null) {
            return true;
          }
        }
      }
    }
  }
  return false;
}


export const getDateAsString = (str: string | 'Не завершено' | 'В процессе'): string => {
  if (str === 'Не завершено' || str === 'В процессе') return str;

  const date = new Date(str);

  const options = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };

  return date.toLocaleString('ru-RU', options);
}
